import { Center, RingProgress, Text } from "@mantine/core";
import { getScoreThreshold } from "../../constants/scoreThresholds";

interface ScoreGaugeProps {
    /** Normalized 0-1 score. Renders nothing when null/undefined unless emptyLabel is set. */
    score: number | null | undefined;
    size?: number;
    thickness?: number;
    /** When score is null/undefined and this is set, renders a gray empty ring with this label instead of returning null. */
    emptyLabel?: string;
}

/**
 * Small RingProgress score gauge, colored via the shared threshold system
 * (src/constants/scoreThresholds.ts) so a color/threshold change only has to happen in one
 * place. Positioning (absolute corner overlay, inline table cell, etc.) is the caller's
 * responsibility — this component only renders the ring itself. Driven entirely by a score
 * value the caller already has (e.g. from list/page data) — never fetches its own data.
 */
export function ScoreGauge({ score, size = 45, thickness = 4, emptyLabel }: ScoreGaugeProps) {
    if (score === null || score === undefined) {
        if (!emptyLabel) {
            return null;
        }
        return (
            <RingProgress
                size={size}
                thickness={thickness}
                roundCaps
                sections={[{ value: 100, color: "gray.3" }]}
                label={
                    <Center>
                        <Text size="xs" fw={700} c="dimmed" style={{ fontSize: 9 }}>
                            {emptyLabel}
                        </Text>
                    </Center>
                }
            />
        );
    }

    const color = getScoreThreshold(score).brandColor;

    return (
        <RingProgress
            size={size}
            thickness={thickness}
            roundCaps
            sections={[{ value: score * 100, color }]}
            label={
                <Center>
                    <Text size="xs" fw={700} style={{ fontSize: 10 }}>
                        {Math.round(score * 100)}%
                    </Text>
                </Center>
            }
        />
    );
}
